import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductContract } from '../../contracts/product';
import AddIcon from '@mui/icons-material/Add';
import './CreateSubscriptionForm.css';

interface CreateSubscriptionFormProps {
    products: ProductContract[];
    onCreate: (productId: string, displayName: string) => Promise<void>;
    subscriptionLimit: number;
    currentCount: number;
}

interface FormErrors {
    name?: string;
}

const CreateSubscriptionForm: React.FC<CreateSubscriptionFormProps> = ({
    products,
    onCreate,
    subscriptionLimit,
    currentCount,
}) => {
    const { t } = useTranslation();
    const [name, setName] = useState<string>('');
    const [isCreating, setIsCreating] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});

    // Check if limit reached
    const canCreateSubscription = currentCount < subscriptionLimit;

    // Get default product (prefer published state, fallback to first product)
    const defaultProduct = products.find(p => p.state === 'published') || products[0];

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: FormErrors = {};

        if (!name.trim()) {
            newErrors.name = 'validation.subscriptionNameRequired';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const productToUse = defaultProduct?.id || 'product-1';

        setIsCreating(true);
        try {
            await onCreate(productToUse, name.trim());
            
            // Reset form on success
            handleReset();
        } catch (err) {
            console.error('Error creating subscription:', err);
            setErrors({ 
                name: err instanceof Error ? err.message : 'subscriptions.createError'
            });
        } finally {
            setIsCreating(false);
        }
    };

    const handleReset = () => {
        setName('');
        setErrors({});
    };

    if (!canCreateSubscription) {
        return (
            <div className="create-application-limit">
                <p>{t('subscriptions.limitReached', { limit: subscriptionLimit })}</p>
            </div>
        );
    }

    return (
        <div className="create-application-container">
            {isCreating && (
                <div className="fullpage-loader-overlay">
                    <div className="fullpage-loader">
                        <div className="loader-spinner"></div>
                    </div>
                </div>
            )}
            <div className="create-application-form-card">
                <div className="create-application-icon">
                    <AddIcon style={{ fontSize: '3rem', color: '#84cc16' }} />
                </div>
                <h3 className="create-application-form-title">{t('subscriptions.newSubscriptions')}</h3>
                <form onSubmit={handleSubmit} className="create-application-form">
                    <div className="create-application-field">
                        <label className="create-application-label">
                            {t('subscriptions.subscriptionName')} <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setErrors({ ...errors, name: undefined });
                            }}
                            placeholder={t('subscriptions.subscriptionNamePlaceholder')}
                            className={`create-application-input ${
                                errors.name ? 'error' : ''
                            }`}
                            disabled={isCreating}
                            maxLength={100}
                        />
                        {errors.name && (
                            <span className="create-application-error">{t(errors.name)}</span>
                        )}
                    </div>

                    <div className="create-application-actions">
                        <button
                            type="submit"
                            className="create-application-btn primary"
                            disabled={isCreating}
                        >
                            {isCreating ? t('subscriptions.subscribing') : t('subscriptions.subscribe')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateSubscriptionForm;
